import React, { useState, useEffect } from 'react'
import { Flex } from '../../Components/Common/Flex'
import { DataTable } from 'primereact/datatable'
import { useCQuery } from '../../Services/QueryCache'
import { columnProps } from '../../Services/Functions'
import { Column } from 'primereact/column'
import { FloatInput } from '../Gigi/GigiForm/TransferField/InputForm'
import { formatMilliseconds } from '../StatisticheOperatori/Functions'
import { Button } from '../../Components/Common/Button'
import { Tooltip } from 'primereact/tooltip'
import { Text } from '../../Components/Common/Text'
import { nanoid } from 'nanoid'
import { getNestedValue } from '../LineaOccupata/List'
import { useAuth } from '../../Provider/AuthProvider'

const today = new Date()

const RandomColors = ['#23d8dd', '#42f2c4', '#3df783', '#4e77d7', '#bfb782', '#e81537', '#c9e965', '#775247', '#009080', '#ceada2', '#89b835', '#8ae8ea', '#6758d6', '#839ea3', '#394726', '#fb507b', '#5d226d', '#db3afa', '#a1a8e2', '#de557f', '#b38af8', '#d290eb', '#c9e744', '#1159f9', '#f40273', '#a19ef7', '#fad99b', '#cdd99d', '#ccf80a', '#1d675a', '#ef8ff1', '#78562e', '#451800', '#918de4', '#1108bc', '#c931e8', '#4c7d14', '#c2525c', '#1a066d', '#9ad4ac', '#44be24', '#83c042', '#574e37', '#c29545', '#7c09d6', '#aa2faf', '#3ef048', '#0edbf6', '#115f3e', '#1f8dd5', '#2acabd', '#4bf1dd', '#e7d6ce', '#bdac4f', '#fd9e00', '#d5ec6a', '#9820e4', '#2f8a3b', '#52d642', '#3debc5'].sort(() => Math.random() - Math.random())

export const TimeSearch = () => {
  const { user: userObj = {} } = useAuth()
  const { user = {} } = userObj || {}
  const { role } = user || {}
  const [startTime, setStartTime] = useState('00:01')
  const [startDate, setStartDate] = useState(today.getDay() - 3 + '/' + (today.getMonth() + 1) + '/' + today.getFullYear())
  const [endTime, setEndTime] = useState('23:59')
  const [endDate, setEndDate] = useState(today.getDay() - 1 + '/' + (today.getMonth() + 1) + '/' + today.getFullYear())
  const [datesSearch, setDatesSearch] = useState([
    new Date(new Date(startDate.split('/')[2], startDate.split('/')[1] - 1, startDate.split('/')[0], startTime.split(':')[0], startTime.split(':')[1])),
    new Date(new Date(endDate.split('/')[2], endDate.split('/')[1] - 1, endDate.split('/')[0], endTime.split(':')[0], endTime.split(':')[1]))
  ])
  const [selectedUser, setSelectedUser] = useState(role !== 'admin' && role !== 'gestoreH24' && role !== 'gestoreOperatori' ? user.id : false)
  const { data: time = [] } = useCQuery(['timeSearch', selectedUser, datesSearch])
  const { data: users = [] } = useCQuery(['users'])
  const [timeList, setTimeList] = useState([])
  const [activitiesGroupedByConversation, setActivitiesGroupedByConversation] = useState([])
  const [usersIds, setUsersIds] = useState([])
  const [maxTime, setMaxTime] = useState(0)
  const [hoursSum, setHoursSum] = useState(0)
  const [tableState, setTableState] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {
      user: { value: '', matchMode: 'contains' },
      createdAt: { value: '', matchMode: 'contains' },
      endAt: { value: '', matchMode: 'contains' },
      time: { value: '', matchMode: 'contains' }
    }
  })

  useEffect(() => {
    // check if hour format is valid
    const isValidTime = (time) => {
      const timeParts = time.split(':')
      if (timeParts.length !== 2) return false
      if (timeParts[0].length !== 2 || timeParts[1].length !== 2) return false
      return true
    }
    if (!isValidTime(startTime) || !isValidTime(endTime)) return
    const startDateObj = new Date(startDate.split('/')[2], startDate.split('/')[1] - 1, startDate.split('/')[0], startTime.split(':')[0], startTime.split(':')[1])
    const endDateObj = new Date(endDate.split('/')[2], endDate.split('/')[1] - 1, endDate.split('/')[0], endTime.split(':')[0], endTime.split(':')[1])
    setDatesSearch([startDateObj, endDateObj])
  }, [startDate, startTime, endDate, endTime])

  useEffect(() => {
    const timeSum = timeList // filter with datatable filters
      ?.filter((item) =>
        Object.keys(tableState.filters).every((key) => {
          const filterValue = tableState?.filters?.[key]?.value
          if (filterValue) {
            const itemValue = getNestedValue(item, key)
            return itemValue?.toLowerCase()?.includes(filterValue?.toLowerCase())
          }
          return true
        }))
      ?.reduce((acc, curr) => {
        return acc + curr.timeDb
      }, 0)
    setHoursSum(timeSum)
  }, [timeList?.length, tableState.filters])

  useEffect(() => {
    setTimeList(time?.map((time) => ({
      ...time,
      user: users.find(({ id }) => id === time.userId)?.username,
      time: formatMilliseconds(time?.time),
      timeDb: time?.time,
      createdAt: new Date(time.createdAt).toLocaleString('it-IT'),
      endAt: new Date(time.endAt).toLocaleString('it-IT')
    })))
  }, [time?.length])

  useEffect(() => {
    const difference = datesSearch[1].getTime() - datesSearch[0].getTime()
    setMaxTime(difference)
    if ((difference / 1000 / 60 / 60 / 24) > 7 || !time?.length) {
      setActivitiesGroupedByConversation([])
    } else {
      const groupByConversation = time?.reduce((acc, { conversationId, ...rest }) => {
        if (!acc?.find(({ conversationId: c }) => c === conversationId)) acc.push({ conversationId, activities: [] })
        const index = acc.findIndex(({ conversationId: c }) => c === conversationId)
        acc[index].activities.push(rest)
        return acc
      }, [])

      setUsersIds([...new Set(groupByConversation?.map(({ activities }) => activities?.map(act => act.userId))?.flat())])

      const activitiesGroupedByConversation = {}
      groupByConversation.forEach(({ conversationId, activities }) => {
        activitiesGroupedByConversation[conversationId] = []
        activities?.forEach((activity, ind) => {
          if (ind === 0) {
            activitiesGroupedByConversation[conversationId]?.push({ fake: true, time: new Date(activity.createdAt).getTime() - datesSearch[0].getTime() })
          }
          activitiesGroupedByConversation[conversationId]?.push(activity)
          if (activities[ind + 1]) {
            activitiesGroupedByConversation[conversationId]?.push({ fake: true, time: new Date(activities[ind + 1]?.createdAt).getTime() - new Date(activity.endAt).getTime() })
          }
          if (ind === activities.length - 1) {
            activitiesGroupedByConversation[conversationId]?.push({ fake: true, time: datesSearch[1].getTime() - new Date(activity.endAt).getTime() })
          }
        })
      })
      setActivitiesGroupedByConversation(activitiesGroupedByConversation)
    }
  }, [datesSearch[0], datesSearch[1], time?.length])

  return (
    <Flex fw>
      <Flex fw row style={{ marginBottom: 20 }}>
        <Flex row style={{ width: '75%' }}>
          <Flex style={{ width: 350, marginRight: 10 }}>
            <FloatInput
              id='user' value={[selectedUser, setSelectedUser]}
              type='dropdown'
              options={
                role !== 'admin' && role !== 'gestoreH24' && role !== 'gestoreOperatori'
                  ? users.filter(({ id }) => id === userObj.user.id).map(({ id, username }) => ({ label: username, value: id }))
                  : [{ label: 'Tutti', value: false }, ...users.map(({ id, username }) => ({ label: username, value: id }))]
}
              label='Seleziona utente'
            />
          </Flex>
          <FloatInput value={[startDate, setStartDate]} label='Data inizio' type='date' />
          <FloatInput value={[startTime, setStartTime]} label='Ora inizio' type='time' defaultValue='00:01' />
          <FloatInput value={[endDate, setEndDate]} label='Data fine' type='date' />
          <FloatInput value={[endTime, setEndTime]} label='Ora fine' type='time' defaultValue='23:59' />
        </Flex>
        <Flex je row style={{ width: '25%' }}>
          {!!hoursSum && <Text value={'Totale: ' + formatMilliseconds(hoursSum)} bold size={18} />}
        </Flex>
      </Flex>
      <DataTable
        style={{ width: '100%' }} filterDisplay='row' className='TableSignatures'
        value={timeList} emptyMessage='Nessuna attività presente' responsiveLayout='scroll' paginator rows={10}
        first={tableState.first} onPage={setTableState}
        onFilter={(event) => { event.first = 0; setTableState(event) }}
        filters={tableState.filters} totalRecords={timeList.length}
      >
        <Column field='user' {...columnProps('Utente')} />
        <Column field='createdAt' {...columnProps('Inizio')} />
        <Column field='endAt' {...columnProps('Fine')} />
        <Column field='time' {...columnProps('Tempo')} />
        <Column
          style={{ width: 150 }}
          field='conversationId' body={(rowData) =>
            <Button
              icon='fa-duotone fa-external-link' label={'Id: ' + rowData.conversationId} style={{ width: 150 }}
              onClick={() => window.open(window.location.origin + '/operatore/' + rowData.conversationId, '_blank')}
            />}
        />
      </DataTable>
      {!!Object.keys(activitiesGroupedByConversation)?.length &&
        <>
          <Flex as fw js row style={{ marginTop: 20, marginBottom: 10 }}>
            <Text value='Legenda Operatori' bold style={{ width: 220 }} />
            <Flex wrap js row style={{ marginLeft: 20, gap: '0px 10px' }}>
              {usersIds?.map(userId => (
                <Flex key={userId} row>
                  <Text value={users?.find(user => user.id === userId)?.username} />
                  <div style={{ backgroundColor: RandomColors[userId % 50], height: 15, width: 15, marginLeft: 5 }} />
                </Flex>
              ))}
            </Flex>
          </Flex>
          <div style={{ maxHeight: '80vh', overflow: 'scroll', width: '100%', borderBottom: '1px solid black', borderRight: '1px solid black' }}>
            {Object.keys(activitiesGroupedByConversation).map((conversationId) => {
              const activities = activitiesGroupedByConversation[conversationId]
              return (
                <Flex fw row key={conversationId} js style={{ height: 26 }}>
                  <Button
                    icon='fa-duotone fa-external-link' label={'Id: ' + conversationId} style={{ width: 150, height: 25 }}
                    onClick={() => window.open(window.location.origin + '/operatore/' + conversationId, '_blank')}
                  />
                  <Flex fw row style={{ borderTop: '1px solid black' }} as js>
                    {activities.map(({ id, userId, time, createdAt, endAt, fake }, ind) => {
                      const targetId = nanoid()
                      return (
                        <React.Fragment key={ind}>
                          <a
                            style={{
                              backgroundColor: fake ? 'transparent' : RandomColors[userId % 50],
                              width: time / maxTime * 100 + '%',
                              height: 25
                            }}
                            {...!fake
                              ? {
                                  className: 'custom-target-icon' + targetId,
                                  'data-pr-tooltip': users?.find(user => user.id === userId)?.username + ' - ' + new Date(createdAt)?.toLocaleString() + ' - ' + new Date(endAt)?.toLocaleString(),
                                  'data-pr-position': 'right',
                                  'data-pr-at': 'right-180 top-5',
                                  'data-pr-my': 'left center-2'
                                }
                              : {}}
                          />
                          <Tooltip target={'.custom-target-icon' + targetId} />
                        </React.Fragment>
                      )
                    })}
                  </Flex>
                </Flex>
              )
            })}
          </div>
          <Flex fw jb row style={{ paddingLeft: 150 }}>
            <Text value={datesSearch[0]?.toLocaleString()} />
            <Text value={datesSearch[1]?.toLocaleString()} />
          </Flex>
        </>}
    </Flex>
  )
}
